"use client"

import { createContext, useCallback, useEffect, useState } from "react"
import { api, clearTokens, restoreSession, setAccessToken } from "@/lib/api"
import { getSafeRedirectPath } from "@/lib/auth-redirect"
import type { AuthUser } from "@/lib/types/auth"

export interface AuthContextValue {
  user: AuthUser | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (
    credentials: { login_identity: string; password: string },
    rememberMe?: boolean,
  ) => Promise<{ success: boolean; error?: string }>
  register: (data: {
    email: string
    password: string
    password_confirmation: string
  }) => Promise<{ success: boolean; error?: string }>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
}

export const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const refreshUser = useCallback(async () => {
    try {
      const response = await api.get<{ user: AuthUser }>("/api/v1/me")
      if (response.data?.user) {
        setUser(response.data.user)
      }
    } catch {
      setUser(null)
      clearTokens()
    }
  }, [])

  useEffect(() => {
    const init = async () => {
      setIsLoading(true)
      const restored = await restoreSession()
      if (restored) {
        await refreshUser()
      }
      setIsLoading(false)
    }
    void init()
  }, [refreshUser])

  useEffect(() => {
    const handleUnauthorized = () => {
      setUser(null)
      clearTokens()
      const path = window.location.pathname
      if (path.startsWith("/login") || path.startsWith("/auth")) {
        return
      }
      const currentPath = window.location.pathname + window.location.search
      const redirect = getSafeRedirectPath(currentPath)
      const params = new URLSearchParams()
      if (redirect !== "/") {
        params.set("redirect", redirect)
      }
      window.location.href = params.toString() ? `/login?${params.toString()}` : "/login"
    }

    window.addEventListener("api:unauthorized", handleUnauthorized)
    return () => window.removeEventListener("api:unauthorized", handleUnauthorized)
  }, [])

  const login = useCallback(async (
    credentials: { login_identity: string; password: string },
    rememberMe = true,
  ) => {
    try {
      const response = await api.post<{ access_token: string; user: AuthUser }>(
        "/api/v1/auth/login",
        { ...credentials, remember_me: rememberMe },
      )
      if (response.data?.access_token) {
        setAccessToken(response.data.access_token, rememberMe)
        setUser(response.data.user)
        return { success: true }
      }
      return { success: false, error: response.message ?? "Ошибка входа" }
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : "Ошибка входа" }
    }
  }, [])

  const register = useCallback(async (data: {
    email: string
    password: string
    password_confirmation: string
  }) => {
    try {
      const response = await api.post<{ access_token: string; user: AuthUser }>(
        "/api/v1/auth/register",
        { ...data, registration_source: "markirovka" },
      )
      if (response.data?.access_token) {
        setAccessToken(response.data.access_token, true)
        setUser(response.data.user)
        return { success: true }
      }
      const errorMsg = response.errors
        ? Object.values(response.errors).flat().join(", ")
        : (response.message ?? "Ошибка регистрации")
      return { success: false, error: errorMsg }
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : "Ошибка регистрации" }
    }
  }, [])

  const logout = useCallback(async () => {
    try {
      await api.post("/api/v1/auth/logout", {})
    } catch {
      // ignore
    } finally {
      setUser(null)
      clearTokens()
      window.location.href = "/"
    }
  }, [])

  return (
    <AuthContext
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        register,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext>
  )
}
